import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './icons.tsx';
import { Kbd } from './layout.tsx';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** `danger` = the confirming button of a destructive dialog; `danger-outline` = a destructive action
   *  offered on a screen (it opens that dialog), so it doesn't shout louder than the primary action. */
  variant?: 'primary' | 'secondary' | 'danger' | 'danger-outline' | 'ghost';
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: IconName | undefined;
  /** visual shortcut hint; the accessible name stays the label (pair with aria-keyshortcuts) */
  kbd?: ReactNode;
}

const VARIANT: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary text-primary-contrast hover:bg-primary-strong border-transparent',
  secondary: 'bg-surface text-text border-border hover:bg-surface-muted',
  danger: 'bg-danger text-white border-transparent hover:opacity-90',
  'danger-outline': 'bg-surface text-danger border-danger/50 hover:bg-danger/10',
  // text button: primary colour so it reads as an action, not as a label next to one
  ghost: 'bg-transparent text-primary border-transparent hover:bg-primary-soft',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  kbd,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-md border font-semibold whitespace-nowrap transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-10 px-4 text-base',
        VARIANT[variant],
        className,
      ].join(' ')}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-e-transparent motion-reduce:animate-none"
        />
      ) : icon ? (
        <Icon name={icon} size={size === 'sm' ? 16 : 18} />
      ) : null}
      {children}
      {kbd ? <Kbd>{kbd}</Kbd> : null}
    </button>
  );
}
